import { Divider, Modal } from "antd";
import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import UploadForm from "~Common/Components/UploadForm";
import PermissionCheck from "~Common/PermissionCheck";
import { useUserPermissions } from "~context/UserPermissionsContext";
import type { ImageBaseTypes } from "~enums/imageBaseTypes.ts";
import type { ImageTypes } from "~enums/imageTypes.ts";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import type { Image } from "~types/image.ts";
import "./LogoUploadModal.scss";

type Props = {
  images: Array<Image>;
  imageType: ImageTypes;
  imageBaseType: ImageBaseTypes;
  dataId: string;
  refetch: () => void;
  uploadUrl?: string;
};

export const LogoUploadModal = ({ images, imageType, imageBaseType, dataId, refetch, uploadUrl }: Props) => {
  const [open, setOpen] = useState(false);
  const { formatMessage } = useIntl();
  const image = images.find((img: Image) => img.imageType === imageType && img.baseType === imageBaseType);
  const { hasPermission } = useUserPermissions();
  const hasEditPermission = hasPermission(PermissionNames.ORGANIZATION, PermissionOperations.UPDATE);
  const uploadButtonClickHandler = useMemo(
    () => hasEditPermission ? () => setOpen(true) : undefined,
    [hasEditPermission],
  );

  return (
    <>
      <Modal
        title={
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <i className={`fas fa-camera`} />
            {formatMessage({ id: "upsert.organization.logo.modal.title" })}
          </div>
        }
        open={open}
        width={350}
        okButtonProps={{ hidden: true }}
        cancelButtonProps={{ hidden: true }}
        onCancel={() => {
          setOpen(false);
        }}
      >
        <>
          <div>{formatMessage({ id: "upsert.organization.logo.modal.description" })}</div>
          <Divider style={{ margin: "5px 0" }} />
          <UploadForm
            uploadUrl={uploadUrl}
            className="image-upload-modal"
            imageType={imageType}
            dataId={dataId}
            image={image}
            imageFor={imageBaseType}
            refetch={refetch}
            permissionKey={PermissionNames.ORGANIZATION}
          />
        </>
      </Modal>
      <div
        className="image-upload-modal-button"
        onClick={uploadButtonClickHandler}
      >
        {image ? (
          <img className="logo-preview" width={80} height={80} alt={imageType} src={`${image.url}?size=200`} />
        ) : (
          <div className="upload-logo-preview">
            <div className="upload-logo-text">
              <div>{formatMessage({ id: "upload.logo" })}</div>
            </div>
            <div className="upload-logo-icon">
              <i className={`fas fa-2x fa-shield-halved`} />
            </div>
          </div>
        )}
        <PermissionCheck permissionKeys={[PermissionNames.ORGANIZATION]} permissionOptions={[PermissionOperations.UPDATE]}>
          <div className="camera-icon">
            <i className={`fa-solid fa-2xs fa-camera`} />
          </div>
        </PermissionCheck>
      </div>
    </>
  );
};
